import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import CloseIcon from "@mui/icons-material/Close";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import ErrorRoundedIcon from "@mui/icons-material/ErrorRounded";
import SwapCallsIcon from "@mui/icons-material/SwapCalls";
import { styled } from "@mui/material/styles";
import def from "csi/def";
import { View } from "csi/def/View";
import DojoView from "csi/page/DojoView";
import reflection from "csi/reflection";
import { useStore } from "csi/state/hooks";
import { DEFAULT_FORM_ID } from "csi/state/viewUtils";
import { GridColumnProps } from "designSystem/components/grid/GridContainer";
import ContentCopyIcon from "designSystem/components/icon/ContentCopyIcon";
import SwapCallsHorizIcon from "designSystem/components/icon/SwapCallsHorizIcon";
import SearchSingleSelect, { onSearchComponentItemDataStore } from "designSystem/components/select/SearchSingleSelect";
import { DndOrderedStore, DndOrderedStoreContext, useDndOrderedOnItem } from "designSystem/dnd/index";
import { useHover, useSyncLocalStore } from "designSystem/hooks";
import {
	AspectBox,
	Box,
	Button,
	ColorChip,
	CssGrid,
	DataGridCardProps,
	FlexRowCenter,
	Grid,
	GridColumn,
	GridItemGrow,
	IconButton,
	MasonryGrid,
	MenuItemWithIcon,
	MoreHorizMenu,
	OverflowText,
	PopperInContainer,
	Text
} from "designSystem/index";
import { useLocale } from "designSystem/locale";
import { black, grey, red } from "designSystem/styles/color";
import { STANDARD_BORDER_STYLE, disabledTextStyle } from "designSystem/styles/style-functions";
import { observer } from "mobx-react-lite";
import React, { FunctionComponent, MouseEventHandler, useRef, useState } from "react";
import { mergeRefs } from "react-merge-refs";
import ProductType from "src/Apparel/ProductType";
import { ColorComponentItem, PRODUCT_PRESENTATION_SET, getProductColorSpecImage } from "./PPService";
import { useSetEditStore } from "./store/SetEditStore";

const PREFIX = "SetEditForm";

const classes = {
	arrow: `${PREFIX}arrow`,
	card: `${PREFIX}card`,
	cardDragging: `${PREFIX}cardDragging`,
	cardHasViolation: `${PREFIX}cardHasViolation`,
	cardSpacer: `${PREFIX}cardSpacer`,
	disabledText: `${PREFIX}disabledText`,
	errorIcon: `${PREFIX}errorIcon`,
	grid: `${PREFIX}grid`,
	gridIcon: `${PREFIX}gridIcon`,
};

const StyledGridColumn = styled(GridColumn)((
	{
		theme,
	},
) => ({
	[`& .${classes.arrow}`]: {
		color: black[100],
	},

	[`& .${classes.card}`]: {
		alignItems: "center",
		backgroundColor: grey[200],
		borderRadius: 3,
		boxSizing: "border-box",
		height: 26,
		paddingBottom: 3,
		paddingLeft: theme.spacing(1),
		paddingRight: theme.spacing(1),
		paddingTop: 3,
		position: "relative",
	},

	[`& .${classes.cardDragging}`]: {
		background: "transparent",
		borderColor: grey[200],
		borderStyle: "dashed",
		borderWidth: 1,

		"& > *": {
			visibility: "hidden",
		},
	},

	[`& .${classes.cardHasViolation}`]: {
		backgroundColor: red[100],
	},

	[`& .${classes.cardSpacer}`]: {
		marginRight: theme.spacing(1),
	},

	[`& .${classes.disabledText}`]: {
		...disabledTextStyle(theme),
	},

	[`& .${classes.errorIcon}`]: {
		color: red[500],
		marginRight: theme.spacing(2),
	},

	[`& .${classes.grid}`]: {
		// fix height so that even if MasonryGrid shrinks due to scrollbar, the overall height stays
		// the same so no jitter can happen when scrollbar appears, triggering MasonryGrid to shrink,
		// this removing the scrollbar
		border: STANDARD_BORDER_STYLE,
		borderRadius: 3,
		flexShrink: 0,
		height: 295,
		marginTop: theme.spacing(2),
		marginBottom: theme.spacing(4),
		position: "relative",
	},

	[`& .${classes.gridIcon}`]: {
		position: "absolute",
		right: theme.spacing(2),
		top: theme.spacing(2),
	},
}));

const SetEditMasonryGrid = observer(() => {
	const editStore = useSetEditStore();
	const { imageList, leftToRight } = editStore;
	const { disabledText } = classes;

	return (
		<MasonryGrid
			emptyNode={(
				<Text
					className={disabledText}
					res="PPNoItemsInSet"
					variant="h5"
				/>
			)}
			imageList={imageList}
			topDown={!leftToRight}
		/>
	);
});

const SetEditMasonryGridWithMenu = observer(() => {
	const editStore = useSetEditStore();
	const hoverRef = useRef(null);
	const hovering = useHover(hoverRef);
	const { hasItems, leftToRight, setLeftToRight } = editStore;

	const {
		grid,
		gridIcon,
	} = classes;

	return (
		<Box className={grid} {...{ ref: hoverRef }}>
			<AspectBox resolution={1600}>
				<SetEditMasonryGrid />
			</AspectBox>
			{hasItems && (
				<MoreHorizMenu
					className={gridIcon}
					design="outline"
					hovering={hovering}
					round
					shadow
					showOnHover
				>
					<MenuItemWithIcon
						onClick={() => setLeftToRight(true)}
						res="LeftToRight"
						selected={leftToRight}
					>
						<SwapCallsHorizIcon />
					</MenuItemWithIcon>
					<MenuItemWithIcon
						onClick={() => setLeftToRight(false)}
						res="TopToBottom"
						selected={!leftToRight}
					>
						<SwapCallsIcon />
					</MenuItemWithIcon>
				</MoreHorizMenu>
			)}
		</Box>
	);
});

export const SetEditColorCard: FunctionComponent<DataGridCardProps<ColorComponentItem>> = ({ item }) => {
	const { hasViolation, href, res } = item;

	const loc = useLocale();
	const name = loc.getComponentItemName(item);

	const { disabledText } = classes;

	return (
		<Grid
			alignItems="center"
			container
			spacing={1}
			wrap="nowrap"
		>
			<Grid item>
				<ColorChip
					hasViolation={hasViolation}
					isDefault={!!res}
					src={href}
				/>
			</Grid>
			<GridItemGrow>
				<OverflowText
					className={hasViolation ? disabledText : ""}
					lineHeight={16}
					res={res}
					text={!!res ? null : name}
				/>
			</GridItemGrow>
		</Grid>
	);
};

interface SetEditItemProps {
	/** index of item in set */
	index: number;
}

const SetEditItem: FunctionComponent<SetEditItemProps> = observer((props) => {
	const { index } = props;

	const loc = useLocale();
	const [anchorEl, setAnchorEl] = useState(null);
	const { cache, copyItem, deleteItem, getItemByIndex, setItemColor } = useSetEditStore();
	const item = getItemByIndex(index);
	const { colorStore, colorUrl, hasViolation, productUrl } = item;
	const product = cache.get(productUrl);
	const color = cache.get(colorUrl);
	const imageSrc = getProductColorSpecImage(color, cache);
	const { browserStore } = useStore();
	const [isDragging, dragItemRef, dragHandleRef, dropRef] = useDndOrderedOnItem(index);
	const supportColor = ProductType.hasColor(product);
	const isDefault = productUrl === color.$URL;
	const titleForButton = isDefault ? loc.resource("Default") : loc.getNodeName(color);
	const { arrow, card, cardDragging, cardHasViolation, cardSpacer, errorIcon } = classes;

	const onClickColorChip: MouseEventHandler = (event) => {
		setAnchorEl(anchorEl ? null : event.currentTarget);
	};

	const onSelectColor = (value?: string) => {
		anchorEl?.focus();

		if (value) {
			setItemColor(item, value);
		}

		setAnchorEl(null);
	};

	return (
		<div
			className={`${card} ${isDragging ? cardDragging : ""} ${hasViolation ? cardHasViolation : ""}`}
			ref={mergeRefs([dropRef, dragItemRef])}
		>
			<Grid
				alignItems="center"
				container
				spacing={1}
				wrap="nowrap"
			>
				<Grid item>
					<IconButton
						design="dragHandle"
						ref={dragHandleRef}
						size="small"
						tabIndex={-1}
					>
						<DragIndicatorIcon />
					</IconButton>
				</Grid>
				{supportColor && (
					<Grid item>
						<FlexRowCenter>
							{hasViolation &&
								<ErrorRoundedIcon className={errorIcon} />
							}
							<Button
								className={cardSpacer}
								design="defaultOnGrey"
								endIcon={<ArrowDropDownIcon className={arrow} />}
								onClick={onClickColorChip}
								size="small"
								title={titleForButton}
							>
								<ColorChip
									hasViolation={hasViolation}
									isDefault={isDefault}
									src={imageSrc}
								/>
							</Button>
						</FlexRowCenter>
						<PopperInContainer anchorEl={anchorEl} disablePortal>
							<SearchSingleSelect<ColorComponentItem>
								dataGridProps={{
									CardComponent: SetEditColorCard,
									store: colorStore,
								}}
								inPopup
								maxHeight={300}
								onSearchStore={onSearchComponentItemDataStore}
								onSelect={onSelectColor}
								value={colorUrl}
								width={240}
							/>
						</PopperInContainer>
					</Grid>
				)}
				<GridItemGrow>
					<OverflowText
						className={cardSpacer}
						semiBold
						text={loc.getNodeName(product)}
					/>
				</GridItemGrow>
				<Grid item>
					<IconButton
						design="defaultOnGrey"
						onClick={() => copyItem(index)}
						size="small"
					>
						<ContentCopyIcon />
					</IconButton>
				</Grid>
				<Grid item>
					<IconButton
						design="defaultOnGrey"
						onClick={() => deleteItem(index)}
						size="small"
					>
						<CloseIcon />
					</IconButton>
				</Grid>
			</Grid>
		</div>
	);
});

const SetEditFormView = () => {
	const { formData, formFieldChanged, setUrl } = useSetEditStore();
	const rc = reflection.getClass(PRODUCT_PRESENTATION_SET);
	const defView = def.getClassViewDef(rc, DEFAULT_FORM_ID) as View;
	const viewParams = {
		bFocused: true,
		defView,
		onAfterChangeField: () => formFieldChanged(),
	};

	defView.setFormData(formData);

	return (
		<DojoView
			className="csi-form-view-flat"
			nodeUrl={setUrl}
			viewId={DEFAULT_FORM_ID}
			viewNodeType={PRODUCT_PRESENTATION_SET}
			viewParams={viewParams}
		/>
	);
};

const SetEditItems = observer(() => {
	const editStore = useSetEditStore();
	const { items, onDropItem } = editStore;
	const dndStore = useSyncLocalStore(() => new DndOrderedStore("PPSetEditItems", onDropItem));

	return (
		<DndOrderedStoreContext.Provider value={dndStore}>
			<CssGrid
				columns={1}
				dnd
				spacing={2}
			>
				{
					items.map((item, i) => (
						<SetEditItem index={i} key={i} />
					))
				}
			</CssGrid>
		</DndOrderedStoreContext.Provider>
	);
});

const SetEditForm: FunctionComponent<GridColumnProps> = observer((props) => {
	const editStore = useSetEditStore();

	if (!editStore.formData) {
		return null;
	}

	return (
		<StyledGridColumn
			spacingAround={3}
			// TODO: use scroll prop instead
			sx={{ overflowY: "scroll", height: "100%" }}
			{...props}
		>
			<SetEditFormView />
			<SetEditMasonryGridWithMenu />
			<SetEditItems />
		</StyledGridColumn>
	);
});

export default SetEditForm;